import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { format, parseISO, differenceInDays } from 'date-fns';
import { downloadSupervisorVisitDoc } from '../lib/generateSupervisorVisitDoc';
import DataTable from '../components/DataTable';
import SupervisionShortForm from '../components/clinical/SupervisionShortForm';
import { KIND_LABEL } from '../lib/supervision';
import { isClosedClient } from '../lib/serviceModel';

// Staff-supervision visits live on the one-page short form; the RN client
// review (and the July-era visits written on it) use the comprehensive form.
const isShort = (v) => v.form_kind === 'short' || (v.status !== 'complete' && v.subject_kind && v.subject_kind !== 'client');

// ── Module-scope helpers ───────────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return d; }
}

function visitStatus(v) {
  if (v.status === 'complete') return 'complete';
  if (!v.next_visit_due) return 'draft';
  const days = differenceInDays(parseISO(v.next_visit_due), new Date());
  if (days < 0) return 'overdue';
  if (days <= 14) return 'due_soon';
  return 'draft';
}

function StatusBadge({ visit }) {
  const s = visitStatus(visit);
  const map = {
    complete:  { label: 'Complete',  bg: 'var(--success-bg)', color: 'var(--success)', border: 'var(--success-border)' },
    overdue:   { label: 'Overdue',   bg: 'var(--danger-bg)', color: 'var(--danger)', border: 'var(--danger-border)' },
    due_soon:  { label: 'Due soon',  bg: 'var(--warn-bg)', color: 'var(--warn)', border: 'var(--warn-border)' },
    draft:     { label: 'Draft',     bg: 'var(--bg2)', color: 'var(--text3)', border: 'var(--border2)' },
  };
  const { label, bg, color, border } = map[s] || map.draft;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 5,
      background: bg, color, border: `1px solid ${border}`, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

const FILTERS = ['All', 'Due soon', 'Overdue', 'Complete'];

// Module-scope helpers for the expanded "Quick view" panel
const fmtLabel = (s) => s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—';
const stars = (n) => n ? '★'.repeat(n) + '☆'.repeat(5 - n) + ` (${n}/5)` : '—';

const TYPE_LABELS = {
  initial_visit:     'Initial visit',
  gapp_pss_biweekly: 'GAPP — PSS/family caregiver',
  gapp_rn_monthly:   'GAPP — RN monthly assessment',
  scheduled:         'Scheduled',
  unannounced:       'Unannounced',
  follow_up:         'Follow-up',
};

export default function SupervisorVisits() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [visits,      setVisits]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState('All');
  const [search,      setSearch]      = useState('');
  const [sortKey,     setSortKey]     = useState('next_visit_due');
  const [sortAsc,     setSortAsc]     = useState(true);
  const [expandedId,  setExpandedId]  = useState(null);
  const [openShort,   setOpenShort]   = useState(null); // supervisor_visits id

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('supervisor_visits')
      .select(`*, clients(first_name, last_name, client_id, doctor_name, pa_expiry_date, current_status)`)
      .neq('status', 'archived')
      .order('next_visit_due', { ascending: true });
    // Closed clients (discharged, on hold, …) are off supervision.
    if (!error) setVisits((data || []).filter(v => !isClosedClient(v.clients)));
    setLoading(false);
  };

  // Lightweight direct updates — deliberately not routed through
  // SupervisorVisitForm.js's completeVisit() flow, which would revert a
  // completed visit to Draft and re-trigger the whole completion sequence.
  const markResolved = async (v) => {
    if (!window.confirm(`Mark the corrective action for ${v.client_name || v.clients?.first_name} as resolved?`)) return;
    await supabase.from('supervisor_visits').update({
      corrective_action_resolved_at: new Date().toISOString(),
      corrective_action_resolved_by: profile?.full_name || 'Staff',
    }).eq('id', v.id);
    load();
  };

  const rnCountersign = async (v) => {
    if (!window.confirm(`Countersign this visit as ${profile?.full_name || 'yourself'}?`)) return;
    await supabase.from('supervisor_visits').update({
      rn_countersigned_at: new Date().toISOString(),
      rn_countersigned_by: profile?.full_name || 'Staff',
    }).eq('id', v.id);
    load();
  };

  const sorting = useMemo(() => sortKey ? [{ id: sortKey, desc: !sortAsc }] : [], [sortKey, sortAsc]);
  const handleSortingChange = (updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (!next.length) return;
    setSortKey(next[0].id);
    setSortAsc(!next[0].desc);
  };

  const filtered = useMemo(() => {
    let list = visits;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(v =>
        (v.client_name || '').toLowerCase().includes(q) ||
        (v.client_ref  || '').toLowerCase().includes(q) ||
        (v.supervisor_name || '').toLowerCase().includes(q)
      );
    }
    if (filter !== 'All') {
      list = list.filter(v => {
        const s = visitStatus(v);
        if (filter === 'Due soon') return s === 'due_soon';
        if (filter === 'Overdue')  return s === 'overdue';
        if (filter === 'Complete') return s === 'complete';
        return true;
      });
    }
    return [...list].sort((a, b) => {
      let av = a[sortKey] || '', bv = b[sortKey] || '';
      return sortAsc ? (av < bv ? -1 : av > bv ? 1 : 0) : (av > bv ? -1 : av < bv ? 1 : 0);
    });
  }, [visits, filter, search, sortKey, sortAsc]);

  // Stats
  const today = new Date();
  const total      = visits.length;
  const dueSoon    = visits.filter(v => visitStatus(v) === 'due_soon').length;
  const overdue    = visits.filter(v => visitStatus(v) === 'overdue').length;
  const complete   = visits.filter(v => v.status === 'complete').length;

  const columns = [
    {
      id: 'client_name', accessorKey: 'client_name', header: 'Patient', size: 170,
      meta: { mobile: 'primary' },
      cell: info => { const v = info.row.original; return v.client_name || `${v.clients?.first_name || ''} ${v.clients?.last_name || ''}`.trim() || '—'; },
    },
    { id: 'client_ref', accessorKey: 'client_ref', header: 'Client ID', size: 110, meta: { mono: true, mobile: 'secondary' }, cell: info => { const v = info.row.original; return v.client_ref || v.clients?.client_id || '—'; } },
    { id: 'supervisor_name', accessorKey: 'supervisor_name', header: 'Supervisor', size: 140, meta: { mobile: 'secondary' }, cell: info => info.getValue() || '—' },
    { id: 'visit_date', accessorKey: 'visit_date', header: 'Visit date', size: 120, meta: { mobile: 'secondary' }, cell: info => fmtDate(info.getValue()) },
    { id: 'visit_type', accessorKey: 'visit_type', header: 'Type', size: 160, meta: { mobile: 'secondary' }, cell: info => { const v = info.row.original; return v.subject_kind && v.subject_kind !== 'client' ? `${KIND_LABEL[v.subject_kind]}${v.backfill && v.status !== 'complete' ? ' · backfill' : ''}` : (TYPE_LABELS[info.getValue()] || info.getValue() || '—'); } },
    { id: 'status', accessorKey: 'status', header: 'Status', size: 110, meta: { mobile: 'primary' }, cell: info => <StatusBadge visit={info.row.original} /> },
    { id: 'next_visit_due', accessorKey: 'next_visit_due', header: 'Next due', size: 120, meta: { mobile: 'primary' }, cell: info => fmtDate(info.getValue()) },
    {
      id: 'actions', header: 'Actions', size: 220, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => {
        const v = info.row.original;
        const isExpanded = expandedId === v.id;
        return (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
            <button
              onClick={() => isShort(v) ? setOpenShort(v.id) : navigate(`/hh/supervisor-visits/${v.id}`)}
              style={{ fontSize: 11, padding: '4px 9px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text2)', cursor: 'pointer' }}>
              {v.status === 'complete' ? 'View' : 'Open'}
            </button>
            {v.status === 'complete' && isShort(v) && v.report_drive_url && (
              <a href={v.report_drive_url} target="_blank" rel="noreferrer"
                style={{ fontSize: 11, padding: '4px 9px', borderRadius: 5, border: '1px solid var(--accent)', background: 'var(--card-bg)', color: 'var(--accent)', fontWeight: 500, textDecoration: 'none' }}>
                Report ↗
              </a>
            )}
            {v.status === 'complete' && !isShort(v) && (<>
              <button
                onClick={() => setExpandedId(isExpanded ? null : v.id)}
                style={{ fontSize: 11, padding: '4px 9px', borderRadius: 5, border: `1px solid ${isExpanded ? 'var(--accent)' : 'var(--border)'}`, background: isExpanded ? 'var(--accent)' : 'var(--card-bg)', color: isExpanded ? '#fff' : 'var(--text2)', cursor: 'pointer' }}>
                {isExpanded ? '▲ Quick' : '▾ Quick'}
              </button>
              <button
                onClick={() => navigate(`/hh/supervisor-visits/${v.id}/report`)}
                style={{ fontSize: 11, padding: '4px 9px', borderRadius: 5, border: '1px solid var(--accent)', background: 'var(--card-bg)', color: 'var(--accent)', cursor: 'pointer', fontWeight: 500 }}>
                Full View
              </button>
              <button
                onClick={() => downloadSupervisorVisitDoc({ visit: v, client: v.clients })}
                title="Download DOCX report"
                style={{ fontSize: 11, padding: '4px 9px', borderRadius: 5, border: '1px solid var(--success)', background: 'var(--card-bg)', color: 'var(--success)', cursor: 'pointer', fontWeight: 600 }}>
                ↓ DOCX
              </button>
            </>)}
            {v.corrective_action_required && !v.corrective_action_resolved_at && (
              <button
                onClick={() => markResolved(v)}
                title="Mark corrective action resolved"
                style={{ fontSize: 11, padding: '4px 9px', borderRadius: 5, border: '1px solid var(--warn)', background: 'var(--card-bg)', color: 'var(--warn)', cursor: 'pointer', fontWeight: 600 }}>
                ✓ Mark Resolved
              </button>
            )}
            {v.lpn_performed_on_rn_behalf && !v.rn_countersigned_at && (
              <button
                onClick={() => rnCountersign(v)}
                title="RN countersign"
                style={{ fontSize: 11, padding: '4px 9px', borderRadius: 5, border: '1px solid var(--accent)', background: 'var(--card-bg)', color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}>
                ✎ RN Countersign
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const renderExpandedRow = (v) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, padding: '12px 4px' }}>
      {/* Clinical */}
      <div style={{ background: 'var(--card-bg)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--border)' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Clinical</div>
        {[
          ['Condition', fmtLabel(v.overall_condition)],
          ['Vitals', v.vital_signs_status || '—'],
          ['Skin', fmtLabel(v.skin_integrity)],
          ['Medication', fmtLabel(v.medication_technique)],
        ].map(([l, val]) => (
          <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '3px 0', borderBottom: '0.5px solid var(--border)' }}>
            <span style={{ color: 'var(--text3)' }}>{l}</span>
            <span style={{ color: 'var(--text)', fontWeight: 500 }}>{val}</span>
          </div>
        ))}
      </div>

      {/* Nurse + Family */}
      <div style={{ background: 'var(--card-bg)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--border)' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Nurse & Family</div>
        {[
          ['Performance', fmtLabel(v.performance_rating)],
          ['Satisfaction', stars(v.satisfaction_overall)],
          ['Consistency', stars(v.satisfaction_consistency)],
          ['Communication', stars(v.satisfaction_communication)],
        ].map(([l, val]) => (
          <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '3px 0', borderBottom: '0.5px solid var(--border)' }}>
            <span style={{ color: 'var(--text3)' }}>{l}</span>
            <span style={{ color: 'var(--text)', fontWeight: 500 }}>{val}</span>
          </div>
        ))}
      </div>

      {/* Physician summary snippet */}
      <div style={{ background: 'var(--card-bg)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--border)' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
          Physician Summary
          {v.physician_name && <span style={{ fontWeight: 400, color: 'var(--text3)', marginLeft: 6, textTransform: 'none' }}>→ {v.physician_name}</span>}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.6, maxHeight: 90, overflow: 'hidden' }}>
          {v.final_summary
            ? v.final_summary.replace(/\*\*/g, '').slice(0, 220) + (v.final_summary.length > 220 ? '…' : '')
            : 'No summary recorded.'}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button
            onClick={() => navigate(`/hh/supervisor-visits/${v.id}/report`)}
            style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 500 }}>
            Full Report →
          </button>
          <button
            onClick={() => downloadSupervisorVisitDoc({ visit: v, client: v.clients })}
            style={{ fontSize: 11, padding: '4px 10px', borderRadius: 5, background: 'var(--card-bg)', color: 'var(--success)', border: '1px solid var(--success)', cursor: 'pointer', fontWeight: 600 }}>
            ↓ DOCX
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1200, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>
            Supervisor Visits
            <span style={{ marginLeft: 10, fontSize: 13, fontWeight: 500, padding: '2px 9px',
              borderRadius: 5, background: 'var(--accent-bg)', color: 'var(--accent)' }}>
              {total}
            </span>
          </h2>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--text3)' }}>
            Supervisory visit records — cadence per client type (Ga. Comp. R. & Regs. R. 111-8-65-.10, GAPP Provider Manual §919)
          </p>
        </div>
        <button
          onClick={() => navigate('/hh/supervisor-visits/new')}
          className="btn btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          New visit
        </button>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total visits',     value: total,    color: 'var(--accent)' },
          { label: 'Due this month',   value: dueSoon,  color: 'var(--warn)' },
          { label: 'Overdue',          value: overdue,  color: 'var(--danger)' },
          { label: 'Completed',        value: complete, color: 'var(--success)' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card" style={{ padding: '14px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Filters + search */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {FILTERS.map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: '5px 13px', borderRadius: 6, fontSize: 12, fontWeight: filter === f ? 600 : 400,
              background: filter === f ? 'var(--accent)' : 'var(--card-bg)',
              color: filter === f ? '#fff' : 'var(--text2)',
              border: `1px solid ${filter === f ? 'var(--accent)' : 'var(--border)'}`,
              cursor: 'pointer',
            }}>{f}</button>
          ))}
        </div>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search patient, ID, supervisor…"
          style={{ marginLeft: 'auto', padding: '6px 12px', borderRadius: 7, border: '1px solid var(--border)',
            background: 'var(--bg2)', color: 'var(--text)', fontSize: 12, width: 240, outline: 'none' }}
        />
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <DataTable
          tableId="supervisor-visits"
          columns={columns}
          data={filtered}
          getRowId={v => v.id}
          onRowClick={v => isShort(v) ? setOpenShort(v.id) : navigate(`/hh/supervisor-visits/${v.id}`)}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          manualSorting
          loading={loading}
          emptyMessage="No supervisor visits found."
          isRowExpanded={v => expandedId === v.id}
          renderExpandedRow={renderExpandedRow}
          maxHeight="calc(100vh - 420px)"
        />
      </div>
      {openShort && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setOpenShort(null)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div className="modal-header"><span>Supervisory visit report</span><button className="btn btn-outline btn-sm" onClick={() => setOpenShort(null)}>Close</button></div>
            <div className="modal-body"><SupervisionShortForm supervisorVisitId={openShort} onDone={() => { setOpenShort(null); load(); }} onCancel={() => setOpenShort(null)} /></div>
          </div>
        </div>
      )}
    </div>
  );
}
